/**
 * Custom React Hook for fetching cash position data
 * Includes loading states and error handling
 */

import { useState, useEffect, useCallback } from 'react';
import {
  fetchCashPosition,
  fetchPnl,
  fetchExpenses,
} from '@/api/cashApi';
import type {
  CashPositionResponse,
  PnlResponse,
  ExpenseSummaryResponse,
} from '@/api/cashApi';

interface CashPnlData {
  cashPosition: CashPositionResponse['data'] | null;
  pnl: PnlResponse['data'] | null;
  expenses: ExpenseSummaryResponse['data'] | null;
}

interface UseCashPositionResult {
  data: CashPnlData;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useCashPosition(): UseCashPositionResult {
  const [data, setData] = useState<CashPnlData>({
    cashPosition: null,
    pnl: null,
    expenses: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [cashPosition, pnl, expenses] = await Promise.all([
        fetchCashPosition(),
        fetchPnl(),
        fetchExpenses(),
      ]);
      setData({ cashPosition, pnl, expenses });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch cash and P&L data';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { data, loading, error, refresh };
}
